import { useState } from 'react';
import type { JobFileEntry } from '@jobos/shared';

import { useInView } from '@/hooks/useInView';
import { useJobFileUrl } from '@/hooks/useJobFiles';
import { hasThumbnail, iconForJobFile } from './jobFileDisplay';

// A card's image area. Fills its container, so the aspect ratio is the caller's
// to set.

function FileIcon({ file, iconClassName }: { file: JobFileEntry; iconClassName: string }) {
  const Icon = iconForJobFile(file);
  return (
    <div className="flex h-full w-full items-center justify-center bg-gray-50">
      <Icon
        className={`${iconClassName} text-gray-400`}
        data-testid="job-file-icon"
        aria-hidden="true"
      />
    </div>
  );
}

/**
 * The lazy part, kept in its own component so a document never pays for an
 * observer or a presign it has no use for.
 *
 * Two things are deferred together: the thumbnail bytes and the signed URL that
 * points at them. The URL is an API call of its own, so `loading="lazy"` alone
 * would still cost one request per file on the initial render.
 */
function LazyThumbnail({
  jobId,
  file,
  iconClassName,
}: {
  jobId: string;
  file: JobFileEntry;
  iconClassName: string;
}) {
  const { ref, inView } = useInView<HTMLDivElement>();
  const [failed, setFailed] = useState(false);
  const { data: url } = useJobFileUrl(jobId, file.id, 'thumb', { enabled: inView && !failed });

  if (failed) return <FileIcon file={file} iconClassName={iconClassName} />;

  return (
    <div ref={ref} className="h-full w-full bg-gray-100">
      {url && (
        <img
          src={url}
          alt={file.fileName}
          loading="lazy"
          onError={() => setFailed(true)}
          className="h-full w-full object-cover"
        />
      )}
    </div>
  );
}

/**
 * @param iconClassName Sizing for the fallback icon. Defaults to the grid card's
 * size; the list view's 36px cell needs a smaller one.
 */
export function JobFileThumbnail({
  jobId,
  file,
  iconClassName = 'h-10 w-10',
}: {
  jobId: string;
  file: JobFileEntry;
  iconClassName?: string;
}) {
  // `hasThumbnail`, not `isImageEntry`: a backfilled PDF is not a photo but does
  // have a first-page render, and showing it beats a generic document icon.
  if (!hasThumbnail(file)) return <FileIcon file={file} iconClassName={iconClassName} />;
  return <LazyThumbnail jobId={jobId} file={file} iconClassName={iconClassName} />;
}
